import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { ShoppingCart, Star, Sparkles, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { getValidImageUrl, DEFAULT_IMAGE } from '../../utils/imageUtils';
import { WishlistButton } from './WishlistButton';

export const ProductCard = ({ product, categoryName = 'Miscellaneous' }) => {
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  
  // Get valid image URL (handles local uploads and missing images)
  const validImageUrl = getValidImageUrl(product.image_url, categoryName, product.id);
  const [imgSrc, setImgSrc] = useState(validImageUrl);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Check if product is sold
  const isSold = product.is_sold === true;

  // Update image if product changes
  useEffect(() => {
    const newValidUrl = getValidImageUrl(product.image_url, categoryName, product.id);
    setImgSrc(newValidUrl);
    setImgError(false);
    setImgLoaded(false);
  }, [product.image_url, product.id, categoryName]);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isSold) {
      toast.error('This item has been sold');
      return;
    }
    
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      return;
    }
    
    const success = await addToCart(product.id);
    if (success) {
      toast.success(`${product.name} added to cart`);
    } else {
      toast.error('Failed to add item to cart');
    }
  };

  const handleImageError = () => {
    if (!imgError) {
      setImgError(true);
      setImgSrc(DEFAULT_IMAGE);
    }
  };

  const handleImageLoad = () => {
    setImgLoaded(true);
  };

  // Truncate description to ~50 chars
  const shortDesc = product.description 
    ? product.description.length > 50 
      ? product.description.substring(0, 50) + '...' 
      : product.description
    : '';

  return (
    <Link to={`/product/${product.id}`} data-testid={`product-card-${product.id}`}>
      <Card className={`card-hover overflow-hidden bg-card border-border h-full ${isSold ? 'opacity-80' : ''}`}>
        <div className="product-image-container relative">
          {/* Loading skeleton */}
          {!imgLoaded && (
            <div className="absolute inset-0 bg-muted animate-pulse" />
          )}
          <img
            src={imgSrc}
            alt={product.name}
            className={`product-image transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'} ${isSold ? 'grayscale' : ''}`}
            loading="lazy"
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
          {/* SOLD Badge - center of image */}
          {isSold && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Badge className="bg-red-600 text-white text-sm px-3 py-1.5 font-bold shadow-lg transform -rotate-12" data-testid="sold-badge">
                SOLD
              </Badge>
            </div>
          )}
          {product.is_bestseller && !isSold && (
            <Badge className="absolute top-2 left-2 bg-accent text-accent-foreground flex items-center gap-1 text-xs" data-testid="bestseller-badge">
              <Star className="h-3 w-3 fill-current" />
              Best
            </Badge>
          )}
          {product.is_new_arrival && !product.is_bestseller && !isSold && (
            <Badge className="absolute top-2 left-2 bg-emerald-600 text-white flex items-center gap-1 text-xs" data-testid="new-arrival-badge">
              <Sparkles className="h-3 w-3" />
              New
            </Badge>
          )}
          {product.is_new_arrival && product.is_bestseller && !isSold && (
            <Badge className="absolute top-2 right-2 bg-emerald-600 text-white flex items-center gap-1 text-xs" data-testid="new-arrival-badge">
              <Sparkles className="h-3 w-3" />
              New
            </Badge>
          )}
          {/* Wishlist Button */}
          {!isSold && (
            <div className="absolute top-2 right-2" style={{ zIndex: 10 }}>
              {!product.is_new_arrival || !product.is_bestseller ? (
                <WishlistButton 
                  itemId={product.id} 
                  itemType="product" 
                  className="bg-white/80 hover:bg-white shadow-sm h-8 w-8"
                />
              ) : null}
            </div>
          )}
        </div>
        <CardContent className="p-2 sm:p-3">
          <h3 className="font-serif font-semibold text-foreground text-xs sm:text-sm line-clamp-2 mb-0.5 sm:mb-1">
            {product.name}
          </h3>
          {shortDesc && (
            <p className="text-[10px] sm:text-xs text-muted-foreground mb-1.5 sm:mb-2 line-clamp-1 hidden sm:block">
              {shortDesc}
            </p>
          )}
          <div className="flex items-center justify-between">
            <span className={`price-tag text-sm sm:text-base font-bold ${isSold ? 'text-muted-foreground line-through' : 'text-primary'}`}>
              ${product.price.toFixed(2)}
            </span>
            <div className="flex items-center gap-1">
              {isSold ? (
                <Badge variant="outline" className="text-red-600 border-red-300 text-[10px] sm:text-xs px-1.5 sm:px-2">Sold</Badge>
              ) : product.affiliate_url ? (
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(product.affiliate_url, '_blank', 'noopener'); }}
                  data-testid={`affiliate-link-${product.id}`}
                >
                  <ExternalLink className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                  Buy
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="btn-accent h-7 w-7 sm:h-8 sm:w-8 p-0"
                  onClick={handleAddToCart}
                  data-testid={`add-to-cart-${product.id}`}
                >
                  <ShoppingCart className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
